import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { ensureProntuarioForRecord } from "@/lib/prontuario/versioning";

export type ProntuarioVersaoWithUser = Database["public"]["Tables"]["prontuario_versoes"]["Row"] & {
  autor?: { id: string; full_name: string | null } | null;
};

export type ProntuarioEventoWithUser = Database["public"]["Tables"]["prontuario_eventos"]["Row"] & {
  user?: { id: string; full_name: string | null } | null;
};

export function useProntuarioTimeline(medicalRecordId?: string) {
  return useQuery({
    queryKey: ["prontuario-timeline", medicalRecordId],
    enabled: Boolean(medicalRecordId),
    queryFn: async () => {
      if (!medicalRecordId) return null;

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error("Usuário não autenticado");

      const { data: record, error: recordError } = await supabase
        .from("medical_records")
        .select("*")
        .eq("id", medicalRecordId)
        .single();

      if (recordError) throw recordError;

      const prontuario = await ensureProntuarioForRecord(record, userData.user.id);

      const { data: versions, error: versionsError } = await supabase
        .from("prontuario_versoes")
        .select("*")
        .eq("prontuario_id", prontuario.id)
        .order("created_at", { ascending: false });

      if (versionsError) throw versionsError;

      const { data: eventos, error: eventosError } = await supabase
        .from("prontuario_eventos")
        .select("*")
        .eq("prontuario_id", prontuario.id)
        .order("created_at", { ascending: false });

      if (eventosError) throw eventosError;

      const authorIds = new Set<string>();
      (versions ?? []).forEach((version) => {
        if (version.autor_id) authorIds.add(version.autor_id);
      });
      (eventos ?? []).forEach((evento) => {
        if (evento.user_id) authorIds.add(evento.user_id);
      });

      let authorsById = new Map<string, { id: string; full_name: string | null }>();
      if (authorIds.size > 0) {
        const { data: profiles, error: profilesError } = await supabase
          .from("profiles")
          .select("id, full_name")
          .in("id", Array.from(authorIds));

        if (profilesError) throw profilesError;
        authorsById = new Map(
          (profiles ?? []).map((profile) => [profile.id, profile]),
        );
      }

      const versionsWithUser = (versions ?? []).map((version) => ({
        ...(version as Database["public"]["Tables"]["prontuario_versoes"]["Row"]),
        autor: version.autor_id ? authorsById.get(version.autor_id) ?? null : null,
      }));

      const eventosWithUser = (eventos ?? []).map((evento) => ({
        ...(evento as Database["public"]["Tables"]["prontuario_eventos"]["Row"]),
        user: evento.user_id ? authorsById.get(evento.user_id) ?? null : null,
      }));

      return {
        prontuarioId: prontuario.id,
        currentVersionId: prontuario.current_version_id,
        versions: versionsWithUser as ProntuarioVersaoWithUser[],
        events: eventosWithUser as ProntuarioEventoWithUser[],
        latestVersion: versionsWithUser[0] ?? null,
      };
    },
  });
}
